import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertOctagon, CalendarClock, FileUp, Flag, GitCompare, Layers, ListTree, Route, Sparkles, Users } from "lucide-react";
import api, { apiError, ruleReasons } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  DEP_TYPE, fmtDate, fmtDateTime, fmtDays, PLAN_ACT_STATUS, PLAN_MILESTONE_STATUS, PLAN_VERSION_STATUS,
  RECOVERY_PLAN_STATUS, RISK_SEVERITY, SCHEDULE_RISK_STATUS,
} from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ReasonsList, ToneChip, UserLabel } from "../../components/common";
import { FinActions } from "../../components/finance";
import { DepBadge, DiffSummary, GanttChart, ValidationList, Variance, VersionChip } from "../../components/planning";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const TABS = [
  { id: "planning", label: "Planning", icon: ListTree },
  { id: "versiones", label: "Versiones", icon: Layers },
  { id: "importar", label: "Importar", icon: FileUp },
  { id: "desviaciones", label: "Desviaciones", icon: CalendarClock },
  { id: "riesgos", label: "Riesgos de retraso", icon: AlertOctagon },
  { id: "recovery", label: "Recovery plans", icon: Route },
  { id: "recursos", label: "Recursos", icon: Users },
];

// ---------------- Diálogo de avance con evidencia ----------------

function ProgressDialog({ pid, activity, open, onClose, onDone }) {
  const [form, setForm] = useState({ progress: activity?.progress ?? 0, evidence: "", actual_finish: "", comment: "" });
  const [busy, setBusy] = useState(false);
  const [reasons, setReasons] = useState(null);
  const needsEvidence = activity?.evidence_required;
  const to100 = Number(form.progress) === 100;
  const submit = async () => {
    setBusy(true);
    setReasons(null);
    try {
      await api.post(`/proyectos/${pid}/planning/actividades/${activity.id}/avance`, { ...form, progress: Number(form.progress) });
      toast.success("Avance registrado con trazabilidad");
      onDone();
    } catch (e) {
      const r = ruleReasons(e);
      if (r) setReasons(r); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  if (!activity) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="progress-dialog">
        <DialogHeader><DialogTitle>Actualizar avance — {activity.wbs} {activity.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Avance actual: <strong>{activity.progress}%</strong>. El avance nunca puede retroceder.
            {needsEvidence && " Esta actividad exige evidencia para registrar avance."}
            {to100 && needsEvidence && " Un 100% no es una evidencia: adjunta el justificante de cierre."}
          </p>
          <div>
            <Label>Nuevo avance (%)</Label>
            <Input type="number" min={0} max={100} value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} data-testid="progress-input" />
          </div>
          <div>
            <Label>Evidencia / referencia {needsEvidence ? "*" : ""}</Label>
            <Textarea value={form.evidence} onChange={(e) => setForm({ ...form, evidence: e.target.value })} placeholder="Acta, certificación, entregable, fotografía…" data-testid="progress-evidence" />
          </div>
          {to100 && (
            <div>
              <Label>Fecha real de fin *</Label>
              <Input type="date" value={form.actual_finish} onChange={(e) => setForm({ ...form, actual_finish: e.target.value })} data-testid="progress-actual-finish" />
            </div>
          )}
          <div>
            <Label>Comentario</Label>
            <Textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="progress-comment" />
          </div>
          {reasons && <ReasonsList reasons={reasons} testid="progress-blocked" />}
          <Button className="w-full" onClick={submit} disabled={busy} data-testid="progress-submit">{busy ? "Registrando…" : "Registrar avance"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Tab: Planning (tabla WBS + Gantt) ----------------

function PlanningTab({ pid, canPlan }) {
  const [view, setView] = useState("tabla");
  const [versionSel, setVersionSel] = useState("vigente");
  const [filters, setFilters] = useState({ owner: "", phase: "", status: "", critical: "" });
  const [progressAct, setProgressAct] = useState(null);
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["plan-activities", pid, versionSel],
    queryFn: async () => (await api.get(`/proyectos/${pid}/planning/actividades`, { params: { version: versionSel } })).data,
  });
  const { data: summary } = useQuery({
    queryKey: ["plan-summary", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/planning`)).data,
  });
  if (!data || !summary) return null;
  const acts = data.activities;
  const owners = [...new Set(acts.map((a) => a.owner_label).filter(Boolean))];
  const phases = [...new Set(acts.map((a) => a.phase).filter(Boolean))];
  const filtered = acts.filter((a) =>
    (!filters.owner || a.owner_label === filters.owner) &&
    (!filters.phase || a.phase === filters.phase) &&
    (!filters.status || a.status === filters.status) &&
    (!filters.critical || (filters.critical === "critica" ? a.critical : !a.critical)));
  const milestones = acts.filter((a) => a.milestone);

  return (
    <div className="space-y-4" data-testid="planning-tab">
      <div className="ts-card flex flex-wrap items-center gap-3 p-4" data-testid="planning-context">
        <div>
          <p className="ts-caption">Versión mostrada</p>
          <Select value={versionSel} onValueChange={setVersionSel}>
            <SelectTrigger className="w-64" data-testid="version-selector"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="vigente" data-testid="version-opt-vigente">Vigente{summary.vigente ? ` (v${summary.vigente.number})` : ""}</SelectItem>
              <SelectItem value="baseline" data-testid="version-opt-baseline">Baseline{summary.baseline ? ` (v${summary.baseline.number}) — inmutable` : ""}</SelectItem>
              {summary.versions.filter((v) => !["vigente"].includes(v.status) && v.id !== summary.baseline?.id).map((v) => (
                <SelectItem key={v.id} value={v.id} data-testid={`version-opt-${v.id}`}>v{v.number} — {PLAN_VERSION_STATUS[v.status]?.label || v.status}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {data.version && <VersionChip version={data.version} testid="planning-version-chip" />}
        {summary.baseline && (
          <p className="text-xs text-slate-500">Baseline v{summary.baseline.number} aprobada{summary.baseline.approved_at ? ` el ${fmtDate(summary.baseline.approved_at)}` : ""} — no se sobrescribe nunca.</p>
        )}
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant={view === "tabla" ? "default" : "outline"} onClick={() => setView("tabla")} data-testid="view-tabla">Tabla WBS</Button>
          <Button size="sm" variant={view === "gantt" ? "default" : "outline"} onClick={() => setView("gantt")} data-testid="view-gantt">Timeline / Gantt</Button>
        </div>
      </div>

      <div className="ts-card flex flex-wrap items-center gap-2 p-3" data-testid="planning-filters">
        <Select value={filters.owner} onValueChange={(v) => setFilters({ ...filters, owner: v === "__all" ? "" : v })}>
          <SelectTrigger className="w-48" data-testid="filter-owner"><SelectValue placeholder="Responsable" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">Todos los responsables</SelectItem>{owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.phase} onValueChange={(v) => setFilters({ ...filters, phase: v === "__all" ? "" : v })}>
          <SelectTrigger className="w-44" data-testid="filter-phase"><SelectValue placeholder="Fase / paquete" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">Todas las fases</SelectItem>{phases.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "__all" ? "" : v })}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">Todos los estados</SelectItem>{Object.entries(PLAN_ACT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.critical} onValueChange={(v) => setFilters({ ...filters, critical: v === "__all" ? "" : v })}>
          <SelectTrigger className="w-40" data-testid="filter-critical"><SelectValue placeholder="Criticidad" /></SelectTrigger>
          <SelectContent><SelectItem value="__all">Todas</SelectItem><SelectItem value="critica">Solo críticas</SelectItem><SelectItem value="no_critica">No críticas</SelectItem></SelectContent>
        </Select>
        <span className="ml-auto font-mono text-xs text-slate-400" data-testid="filter-count">{filtered.length}/{acts.length} actividades</span>
      </div>

      {view === "gantt" && <GanttChart activities={filtered} onSelect={(a) => canPlan && versionSel === "vigente" && setProgressAct(a)} />}

      {view === "tabla" && (
        <div className="ts-card overflow-x-auto" data-testid="wbs-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>WBS</TableHead><TableHead>Actividad</TableHead><TableHead>Responsable</TableHead>
                <TableHead>Inicio → Fin</TableHead><TableHead className="text-right">Duración</TableHead>
                <TableHead className="text-right">Avance</TableHead><TableHead>Estado</TableHead>
                <TableHead>Dependencias</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="py-6 text-center text-sm text-slate-500">Sin actividades para estos filtros.</TableCell></TableRow>}
              {filtered.map((a) => (
                <TableRow key={a.id} data-testid={`wbs-row-${a.wbs.replace(/\./g, "-")}`} className={a.critical ? "bg-red-50/40" : ""}>
                  <TableCell className="font-mono text-xs">{a.wbs}{a.milestone && <Flag className="ml-1 inline h-3 w-3 text-amber-600" />}</TableCell>
                  <TableCell className="max-w-56">
                    <p className="truncate text-sm">{a.name}</p>
                    <p className="text-xs text-slate-400">{a.phase}{a.critical && " · ruta crítica"}{a.evidence_required && " · evidencia obligatoria"}</p>
                  </TableCell>
                  <TableCell className="text-xs">{a.owner_id ? <UserLabel id={a.owner_id} /> : <span className="text-amber-600">{a.owner_label || "sin asignar"}</span>}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDate(a.current_start)} → {fmtDate(a.current_finish)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{a.duration != null ? `${a.duration}d` : "—"}</TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-xs font-semibold">{a.progress}%</span>
                    <span className="mt-0.5 block h-1 w-16 rounded-sm bg-slate-100"><span className="block h-1 rounded-sm bg-blue-600" style={{ width: `${a.progress}%` }} /></span>
                  </TableCell>
                  <TableCell><Chip map={PLAN_ACT_STATUS} value={a.status} testid={`act-status-${a.wbs.replace(/\./g, "-")}`} /></TableCell>
                  <TableCell className="max-w-44">
                    <div className="flex flex-wrap gap-1">
                      {(a.predecessors || []).map((d, i) => <DepBadge key={i} dep={d} testid={`dep-${a.wbs.replace(/\./g, "-")}-${i}`} />)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {canPlan && versionSel === "vigente" && a.status !== "completada" && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setProgressAct(a)} data-testid={`progress-open-${a.wbs.replace(/\./g, "-")}`}>Avance</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="ts-card p-4" data-testid="planning-milestones">
        <p className="mb-2 font-display text-sm font-semibold">Hitos de esta versión ({milestones.length})</p>
        <div className="flex flex-wrap gap-2">
          {milestones.map((m) => (
            <span key={m.id} className="ts-chip ts-chip-warning" title={`Fin: ${fmtDate(m.current_finish)}`} data-testid={`ms-chip-${m.wbs.replace(/\./g, "-")}`}>
              <Flag className="mr-1 h-3 w-3" /> {m.wbs} {m.name} · {fmtDate(m.current_finish)}
            </span>
          ))}
          {milestones.length === 0 && <p className="text-xs text-slate-400">Sin hitos en esta versión.</p>}
        </div>
      </div>

      <ProgressDialog pid={pid} activity={progressAct} open={!!progressAct} onClose={() => setProgressAct(null)}
        onDone={() => { setProgressAct(null); qc.invalidateQueries({ queryKey: ["plan-activities", pid] }); qc.invalidateQueries({ queryKey: ["plan-deviations", pid] }); }} />
    </div>
  );
}

// ---------------- Tab: Versiones ----------------

function VersionesTab({ pid, canPlan }) {
  const qc = useQueryClient();
  const { map } = useUsers();
  const [open, setOpen] = useState(null);
  const { data: summary } = useQuery({ queryKey: ["plan-summary", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/planning`)).data });
  const { data: detail } = useQuery({
    queryKey: ["plan-version", pid, open],
    queryFn: async () => (await api.get(`/proyectos/${pid}/planning/versiones/${open}`)).data,
    enabled: !!open,
  });
  if (!summary) return null;
  const act = async (vid, action, payload) => {
    await api.post(`/proyectos/${pid}/planning/versiones/${vid}/accion`, { action, ...payload });
    toast.success("Acción registrada con trazabilidad");
    qc.invalidateQueries({ queryKey: ["plan-summary", pid] });
    qc.invalidateQueries({ queryKey: ["plan-version", pid] });
    qc.invalidateQueries({ queryKey: ["plan-activities", pid] });
  };
  return (
    <div className="space-y-3" data-testid="versiones-tab">
      <p className="text-xs text-slate-500">Flujo de cada versión: Borrador → Validación → Revisión → Aprobación → Publicada (vigente). La baseline aprobada es inmutable; publicar una versión nunca la sobrescribe.</p>
      {summary.versions.map((v) => (
        <div key={v.id} className="ts-card p-4" data-testid={`version-card-${v.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <VersionChip version={v} testid={`version-chip-${v.id}`} />
              <span className="text-xs text-slate-500">{v.activities_count} actividades · origen {v.source}{v.source_filename ? ` (${v.source_filename})` : ""} · hash {v.source_hash || "—"}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">creada {fmtDate(v.created_at)} por {userName(map, v.created_by)}</span>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(open === v.id ? null : v.id)} data-testid={`version-detail-${v.id}`}>
                {open === v.id ? "Ocultar" : "Detalle"}
              </Button>
            </div>
          </div>
          {v.diff && (v.diff.added.length + v.diff.removed.length + v.diff.modified.length > 0) && (
            <p className="mt-1 text-xs text-slate-500" data-testid={`version-diff-${v.id}`}>
              vs v{v.diff.against ?? "—"}: <span className="text-emerald-700">+{v.diff.added.length} nuevas</span> · <span className="text-red-700">−{v.diff.removed.length} eliminadas</span> · <span className="text-amber-700">~{v.diff.modified.length} modificadas</span>
            </p>
          )}
          {v.reviewed_by && <p className="mt-1 text-xs text-slate-400">Revisada por {userName(map, v.reviewed_by)}{v.review_comment ? `: «${v.review_comment}»` : ""}</p>}
          {v.approved_by && <p className="text-xs text-slate-400">Aprobada por {userName(map, v.approved_by)} el {fmtDate(v.approved_at)}{v.published_at ? ` · publicada el ${fmtDate(v.published_at)}` : ""}</p>}
          {open === v.id && detail && (
            <div className="mt-3 space-y-3 border-t border-slate-100 pt-3" data-testid={`version-open-${v.id}`}>
              <DiffSummary diff={detail.version.diff} validation={detail.version.validation} testid={`version-diff-summary-${v.id}`} />
              <ValidationList validation={detail.version.validation} testid={`version-validation-${v.id}`} />
              {detail.version.diff?.modified?.length > 0 && (
                <div className="max-h-48 space-y-1 overflow-y-auto rounded-sm border border-slate-100 p-2" data-testid={`version-modified-${v.id}`}>
                  {detail.version.diff.modified.slice(0, 20).map((m, i) => (
                    <p key={i} className="text-xs text-slate-600">
                      <span className="font-mono">{m.wbs}</span> {m.name}: {Object.entries(m.changes).map(([k, c]) =>
                        k === "dependencies"
                          ? ` dependencias [${(c.before || []).join(",") || "—"}] → [${(c.after || []).join(",") || "—"}]`
                          : ` ${k} ${String(c.before ?? "—").slice(0, 10)} → ${String(c.after ?? "—").slice(0, 10)}`).join(";")}
                    </p>
                  ))}
                </div>
              )}
              {canPlan && <FinActions actions={detail.actions} onAct={(action, payload) => act(v.id, action, payload)} testid={`version-actions-${v.id}`} />}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------- Tab: Importar ----------------

function ImportarTab({ pid }) {
  const qc = useQueryClient();
  const [step, setStep] = useState("elegir");
  const [sel, setSel] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: templates } = useQuery({
    queryKey: ["plan-templates", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/planning/plantillas`)).data,
  });
  const doImport = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/proyectos/${pid}/planning/importar`, { content: sel.content, kind: "actualizacion", source: "csv", filename: `${sel.id}.csv` });
      setResult(r.data);
      setStep("resultado");
      qc.invalidateQueries({ queryKey: ["plan-summary", pid] });
      qc.invalidateQueries({ queryKey: ["plan-activities", pid] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="importar-tab">
      <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500" data-testid="import-workflow">
        {["Seleccionar archivo", "Mapear", "Validar", "Corregir", "Comparar", "Revisar", "Aprobar", "Publicar"].map((s, i) => (
          <React.Fragment key={s}>
            <span className={`rounded-sm px-2 py-1 ${i === 0 && step === "elegir" ? "bg-blue-700 text-white" : "bg-slate-100"}`}>{s}</span>
            {i < 7 && <span>→</span>}
          </React.Fragment>
        ))}
      </div>
      <p className="text-xs text-slate-500">La importación crea siempre una <strong>nueva versión borrador</strong>: nunca sobrescribe el planning vigente. Columnas mapeadas: wbs · nombre · fase · responsable · inicio · fin · duración · progreso · método de avance · hito · crítica · evidencia requerida · recurso · predecesoras (FS/SS/FF/SF con lag, p. ej. <code>1.1:SS+2</code>).</p>

      {step === "elegir" && (
        <div className="grid gap-3 md:grid-cols-2" data-testid="import-templates">
          {(templates || []).map((t) => (
            <button key={t.id} type="button" onClick={() => { setSel(t); setStep("previsualizar"); }} data-testid={`import-template-${t.id}`}
              className={`ts-card p-4 text-left transition-all hover:border-blue-300 hover:shadow-sm ${t.expect === "valida" || t.expect === "valida_con_diff" ? "" : "border-dashed"}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-display text-sm font-semibold">{t.name}</p>
                <ToneChip label={t.expect === "error_bloqueante" ? "con errores" : "válida"} tone={t.expect === "error_bloqueante" ? "danger" : "success"} />
              </div>
              <p className="mt-1 text-xs text-slate-500">{t.description}</p>
            </button>
          ))}
        </div>
      )}

      {step === "previsualizar" && sel && (
        <div className="ts-card space-y-3 p-4" data-testid="import-preview">
          <p className="font-display text-sm font-semibold">{sel.name}</p>
          <pre className="max-h-64 overflow-auto rounded-sm border border-slate-200 bg-slate-50 p-3 font-mono text-xs" data-testid="import-content">{sel.content}</pre>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep("elegir")} data-testid="import-back">← Volver</Button>
            <Button onClick={doImport} disabled={busy} data-testid="import-submit">{busy ? "Importando y validando…" : "Importar y validar"}</Button>
          </div>
        </div>
      )}

      {step === "resultado" && result && (
        <div className="ts-card space-y-3 p-4" data-testid="import-result">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-display text-sm font-semibold">Importada como versión v{result.number}</p>
            <VersionChip version={result} testid="import-result-status" />
            <span className="text-xs text-slate-400">archivo {result.source_filename} · hash {result.source_hash}</span>
          </div>
          <DiffSummary diff={result.diff} validation={result.validation} testid="import-diff-summary" />
          <ValidationList validation={result.validation} testid="import-validation" />
          {result.validation.errors.length > 0 ? (
            <p className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="import-blocked-note">
              Hay {result.validation.errors.length} errores bloqueantes: esta versión <strong>no puede aprobarse ni publicarse</strong> hasta corregirlos. Corrige el archivo y vuelve a importar (se creará otra versión).
            </p>
          ) : (
            <p className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="import-ok-note">
              Sin errores bloqueantes. La versión está pendiente de revisión humana; después irá a aprobación y solo entonces podrá publicarse. El planning vigente no se ha modificado.
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setStep("elegir"); setSel(null); setResult(null); }} data-testid="import-again">Importar otro archivo</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Tab: Desviaciones (baseline / anterior / actual) ----------------

function DesviacionesTab({ pid }) {
  const { data: d } = useQuery({ queryKey: ["plan-deviations", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/planning/desviaciones`)).data });
  if (!d) return null;
  if (d.status === "sin_planning") return <EmptyState title="Sin planning vigente" description={d.message} testid="deviations-empty" />;
  return (
    <div className="space-y-4" data-testid="desviaciones-tab">
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="ts-card p-4" data-testid="dev-versions">
          <p className="ts-caption">Versiones comparadas</p>
          <p className="mt-1 text-xs">Baseline: <strong>v{d.baseline_version ?? "—"}</strong></p>
          <p className="text-xs">Anterior: <strong>v{d.previous_version ?? "—"}</strong></p>
          <p className="text-xs">Actual: <strong>v{d.current_version}</strong></p>
        </div>
        <div className="ts-card p-4" data-testid="dev-project-delay">
          <p className="ts-caption">Desviación del proyecto</p>
          <p className={`mt-1 font-mono text-2xl font-bold ${(d.project_delay_days ?? 0) > 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtDays(d.project_delay_days)}</p>
          <p className="text-xs text-slate-400">{d.convention}</p>
        </div>
        <div className="ts-card p-4" data-testid="dev-delayed-count">
          <p className="ts-caption">Actividades retrasadas</p>
          <p className="mt-1 font-mono text-2xl font-bold text-slate-900">{d.delayed_activities.length}</p>
          <p className="text-xs text-slate-400">{d.trend}</p>
        </div>
        <div className="ts-card p-4" data-testid="dev-critical-path">
          <p className="ts-caption">Ruta crítica (CPM)</p>
          {d.critical_path.status === "ok" ? (
            <>
              <p className="mt-1 font-mono text-2xl font-bold text-slate-900">{d.critical_path.critical_activities.length}<span className="text-sm font-normal text-slate-400">/{d.critical_path.total_activities}</span></p>
              <p className="truncate font-mono text-xs text-slate-500" title={d.critical_path.critical_activities.join(" → ")}>{d.critical_path.critical_activities.join(" → ")}</p>
            </>
          ) : (
            <p className="mt-1 text-xs font-medium text-amber-700" data-testid="critical-path-insufficient">{d.critical_path.status}{d.critical_path.reason ? ` — ${d.critical_path.reason}` : ""}</p>
          )}
        </div>
      </div>

      <div className="ts-card overflow-x-auto" data-testid="deviations-table">
        <p className="border-b border-slate-100 px-4 py-3 font-display text-sm font-semibold">Baseline vs anterior vs actual — por actividad</p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>WBS</TableHead><TableHead>Actividad</TableHead>
              <TableHead>Baseline fin</TableHead><TableHead>Actual fin</TableHead>
              <TableHead className="text-right">Desv. inicio</TableHead><TableHead className="text-right">Desv. fin</TableHead>
              <TableHead className="text-right">Δ vs anterior</TableHead><TableHead className="text-right">Avance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {d.activities.filter((a) => (a.finish_variance ?? 0) !== 0 || (a.start_variance ?? 0) !== 0 || a.critical).map((a) => (
              <TableRow key={a.wbs} data-testid={`dev-row-${a.wbs.replace(/\./g, "-")}`} className={(a.finish_variance ?? 0) > 0 ? "bg-red-50/40" : ""}>
                <TableCell className="font-mono text-xs">{a.wbs}{a.critical && <span className="ml-1 text-red-600">●</span>}</TableCell>
                <TableCell className="max-w-56 truncate text-xs">{a.name}</TableCell>
                <TableCell className="text-xs">{fmtDate(a.baseline_finish)}</TableCell>
                <TableCell className="text-xs">{fmtDate(a.current_finish)}</TableCell>
                <TableCell className="text-right"><Variance days={a.start_variance} testid={`dev-start-${a.wbs.replace(/\./g, "-")}`} /></TableCell>
                <TableCell className="text-right"><Variance days={a.finish_variance} testid={`dev-finish-${a.wbs.replace(/\./g, "-")}`} /></TableCell>
                <TableCell className="text-right"><Variance days={a.since_previous_finish} testid={`dev-prev-${a.wbs.replace(/\./g, "-")}`} /></TableCell>
                <TableCell className="text-right font-mono text-xs">{a.progress}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="ts-card overflow-x-auto" data-testid="deviations-milestones">
        <p className="border-b border-slate-100 px-4 py-3 font-display text-sm font-semibold">Hitos</p>
        <Table>
          <TableHeader><TableRow><TableHead>Hito</TableHead><TableHead>Baseline</TableHead><TableHead>Previsión</TableHead><TableHead>Real</TableHead><TableHead className="text-right">Desviación</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.milestones.map((m) => (
              <TableRow key={m.id} data-testid={`dev-ms-${m.id}`}>
                <TableCell className="text-xs">{m.critical && <span className="mr-1 text-red-600">●</span>}{m.name}</TableCell>
                <TableCell className="text-xs">{fmtDate(m.baseline_date)}</TableCell>
                <TableCell className="text-xs">{fmtDate(m.forecast_date)}</TableCell>
                <TableCell className="text-xs">{fmtDate(m.actual_date)}</TableCell>
                <TableCell className="text-right"><Variance days={m.variance_days} testid={`dev-ms-var-${m.id}`} /></TableCell>
                <TableCell><Chip map={PLAN_MILESTONE_STATUS} value={m.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------- Tab: Riesgos ----------------

function RiesgosTab({ pid, canPlan }) {
  const qc = useQueryClient();
  const { map, users } = useUsers();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ activity_wbs: "", cause: "", impact: "", owner_id: "", severity: "media", deviation_days: "" });
  const [busy, setBusy] = useState(false);
  const [reasons, setReasons] = useState(null);
  const { data: risks } = useQuery({ queryKey: ["plan-risks", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/planning/riesgos`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["plan-risks", pid] }); qc.invalidateQueries({ queryKey: ["plan-recovery", pid] }); };
  const act = async (rid, action, payload) => {
    await api.post(`/proyectos/${pid}/planning/riesgos/${rid}/accion`, { action, ...payload });
    toast.success("Transición registrada");
    refresh();
  };
  const genRecovery = async (rid) => {
    try {
      await api.post(`/proyectos/${pid}/planning/riesgos/${rid}/recovery`);
      toast.success("La IA ha propuesto alternativas de recuperación (pendientes de revisión humana)");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };
  const createRisk = async () => {
    setBusy(true);
    setReasons(null);
    try {
      await api.post(`/proyectos/${pid}/planning/riesgos`, { ...form, deviation_days: form.deviation_days === "" ? null : Number(form.deviation_days) });
      toast.success("Riesgo registrado y notificado al responsable");
      setCreateOpen(false);
      setForm({ activity_wbs: "", cause: "", impact: "", owner_id: "", severity: "media", deviation_days: "" });
      refresh();
    } catch (e) {
      const r = ruleReasons(e);
      if (r) setReasons(r); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  if (!risks) return null;
  const open = risks.filter((r) => !["cerrado", "mitigado"].includes(r.status));
  return (
    <div className="space-y-3" data-testid="riesgos-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{open.length} riesgos abiertos · {risks.length - open.length} cerrados o mitigados. El motor de reglas genera hallazgos automáticos al publicar planning o registrar avance; también puedes registrar riesgos manuales.</p>
        {canPlan && <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="risk-create-open">Registrar riesgo</Button>}
      </div>
      {risks.length === 0 && <EmptyState title="Sin riesgos de retraso" description="No hay hallazgos abiertos ni históricos en este proyecto." testid="risks-empty" />}
      {risks.map((r) => (
        <div key={r.id} className="ts-card p-4" data-testid={`risk-card-${r.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Chip map={RISK_SEVERITY} value={r.severity} testid={`risk-severity-${r.id}`} />
              <Chip map={SCHEDULE_RISK_STATUS} value={r.status} testid={`risk-status-${r.id}`} />
              {r.deviation_days != null && <Variance days={r.deviation_days} testid={`risk-days-${r.id}`} />}
              {r.activity_wbs && <Link to={`?project=${pid}&tab=planning`} className="font-mono text-xs text-blue-700 hover:underline" data-testid={`risk-activity-${r.id}`}>{r.activity_wbs} {r.activity_name}</Link>}
              {r.milestone_name && <span className="text-xs text-slate-500">Hito: {r.milestone_name}</span>}
            </div>
            <span className="text-xs text-slate-400">{userName(map, r.owner_id)} · vence {fmtDate(r.due_date)}</span>
          </div>
          <p className="mt-1 text-sm text-slate-800">{r.impact}</p>
          {r.cause && <p className="mt-0.5 text-xs text-slate-500">Causa: {r.cause}{r.dependencies ? ` · Dependencias: ${r.dependencies}` : ""}</p>}
          {r.evidence && <p className="mt-0.5 text-xs text-slate-500">Evidencia: {r.evidence}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {canPlan && r.actions?.length > 0 && <FinActions actions={r.actions} onAct={(action, payload) => act(r.id, action, payload)} testid={`risk-actions-${r.id}`} />}
            {canPlan && !["cerrado", "mitigado"].includes(r.status) && (
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => genRecovery(r.id)} data-testid={`risk-recovery-${r.id}`}>
                <Sparkles className="mr-1 h-3 w-3" /> Proponer recovery (IA)
              </Button>
            )}
          </div>
        </div>
      ))}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="risk-create-dialog">
          <DialogHeader><DialogTitle>Registrar riesgo de retraso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>WBS de actividad afectada (opcional)</Label><Input value={form.activity_wbs} onChange={(e) => setForm({ ...form, activity_wbs: e.target.value })} placeholder="p. ej. 5.1" data-testid="risk-wbs" /></div>
            <div><Label>Causa raíz *</Label><Textarea value={form.cause} onChange={(e) => setForm({ ...form, cause: e.target.value })} data-testid="risk-cause" /></div>
            <div><Label>Impacto *</Label><Textarea value={form.impact} onChange={(e) => setForm({ ...form, impact: e.target.value })} data-testid="risk-impact" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Responsable *</Label>
                <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                  <SelectTrigger data-testid="risk-owner"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Severidad</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="risk-severity-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(RISK_SEVERITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Desviación estimada (días)</Label><Input type="number" value={form.deviation_days} onChange={(e) => setForm({ ...form, deviation_days: e.target.value })} data-testid="risk-days-input" /></div>
            {reasons && <ReasonsList reasons={reasons} testid="risk-create-blocked" />}
            <Button className="w-full" onClick={createRisk} disabled={busy} data-testid="risk-create-submit">{busy ? "Registrando…" : "Registrar riesgo"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Tab: Recovery plans ----------------

const REVIEW_ROLES = { planner: "Planner", pm: "Project Manager", cost: "Cost Manager", technical: "Technical Manager" };

function RecoveryTab({ pid, canPlan }) {
  const qc = useQueryClient();
  const { map } = useUsers();
  const [reviewDialog, setReviewDialog] = useState(null); // {plan, role}
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [reasons, setReasons] = useState(null);
  const { data: plans } = useQuery({ queryKey: ["plan-recovery", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/planning/recovery`)).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["plan-recovery", pid] }); qc.invalidateQueries({ queryKey: ["plan-summary", pid] }); qc.invalidateQueries({ queryKey: ["plan-activities", pid] }); };
  const act = async (planId, action, payload = {}) => {
    setBusy(true);
    setReasons(null);
    try {
      await api.post(`/proyectos/${pid}/planning/recovery/${planId}/accion`, { action, ...payload });
      toast.success(action === "incorporar" ? "Plan incorporado como NUEVA versión borrador — sigue el flujo validación → revisión → aprobación → publicación" : "Acción registrada");
      setReviewDialog(null);
      setComment("");
      refresh();
    } catch (e) {
      const r = ruleReasons(e);
      if (r) setReasons(r); else toast.error(apiError(e));
    } finally { setBusy(false); }
  };
  if (!plans) return null;
  return (
    <div className="space-y-3" data-testid="recovery-tab">
      <p className="text-xs text-slate-500">La IA propone alternativas; ninguna modifica el planning hasta que el plan es revisado, aprobado e incorporado como nueva versión (que sigue el flujo normal). El planning vigente nunca se toca desde aquí.</p>
      {plans.length === 0 && <EmptyState title="Sin planes de recuperación" description="Genera uno desde un riesgo de retraso con «Proponer recovery (IA)»." testid="recovery-empty" />}
      {plans.map((p) => (
        <div key={p.id} className="ts-card p-4" data-testid={`recovery-card-${p.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-display text-sm font-semibold">{p.title}</p>
              <Chip map={RECOVERY_PLAN_STATUS} value={p.status} testid={`recovery-status-${p.id}`} />
              <Variance days={p.deviation_days} testid={`recovery-days-${p.id}`} />
              {p.ai_generated && <ToneChip label="IA — propuesta" tone="ai" testid={`recovery-ai-${p.id}`} />}
            </div>
            <span className="text-xs text-slate-400">creado {fmtDate(p.created_at)} por {userName(map, p.created_by)}</span>
          </div>
          <p className="mt-1 text-xs italic text-slate-400">{p.disclaimer}</p>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            {p.alternatives.map((a, i) => (
              <div key={i} className={`rounded-sm border p-3 ${a.recommendation ? "border-blue-300 bg-blue-50/40" : "border-slate-200"}`} data-testid={`recovery-alt-${p.id}-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">{a.type.replace(/_/g, " ")}</p>
                  {a.recommendation && <ToneChip label="recomendada" tone="info" testid={`recovery-rec-${p.id}-${i}`} />}
                </div>
                <p className="mt-1 text-xs text-slate-600">{a.description}</p>
                <div className="mt-2 space-y-0.5 text-xs text-slate-500">
                  <p>Ahorro temporal: <strong className="text-emerald-700">−{a.time_benefit_days} días</strong></p>
                  <p>Coste estimado: <strong>{a.cost_estimate ? `${a.cost_estimate.toLocaleString("es-ES")} €` : "0 €"}</strong></p>
                  <p>Riesgo: {a.risk}</p>
                  <p>Viabilidad: {a.viability}</p>
                  <p>Responsable: <UserLabel id={a.owner_id} /></p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            {Object.entries(REVIEW_ROLES).map(([role, label]) => {
              const rev = p.reviews?.[role];
              return (
                <span key={role} className={`ts-chip ${rev ? "ts-chip-success" : "ts-chip-muted"}`} title={rev ? `${rev.comment} (${fmtDateTime(rev.at)})` : "Pendiente"} data-testid={`recovery-review-${p.id}-${role}`}>
                  {label}: {rev ? `✓ ${userName(map, rev.by)}` : "pendiente"}
                </span>
              );
            })}
          </div>
          {canPlan && (
            <div className="mt-2 flex flex-wrap gap-2">
              {["borrador", "en_revision"].includes(p.status) && ["planner", "pm", "cost"].map((role) => (
                <Button key={role} size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setReviewDialog({ plan: p, role }); setComment(""); setReasons(null); }} data-testid={`recovery-review-open-${p.id}-${role}`}>
                  Revisar como {REVIEW_ROLES[role]}
                </Button>
              ))}
              {["borrador", "en_revision"].includes(p.status) && (
                <Button size="sm" className="h-7 text-xs" onClick={() => act(p.id, "solicitar_aprobacion")} disabled={busy} data-testid={`recovery-submit-${p.id}`}>Solicitar aprobación</Button>
              )}
              {p.status === "aprobado" && (
                <Button size="sm" className="h-7 text-xs" onClick={() => act(p.id, "incorporar")} disabled={busy} data-testid={`recovery-incorporate-${p.id}`}>
                  Incorporar al planning (nueva versión borrador)
                </Button>
              )}
            </div>
          )}
          {reasons && <div className="mt-2"><ReasonsList reasons={reasons} testid={`recovery-blocked-${p.id}`} /></div>}
        </div>
      ))}

      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent data-testid="recovery-review-dialog">
          <DialogHeader><DialogTitle>Revisión {reviewDialog ? REVIEW_ROLES[reviewDialog.role] : ""} — plan de recuperación</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Revisa las alternativas propuestas por la IA. Tu comentario queda registrado con trazabilidad.</p>
            <div><Label>Comentario de revisión *</Label><Textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="recovery-review-comment" /></div>
            {reasons && <ReasonsList reasons={reasons} testid="recovery-review-blocked" />}
            <Button className="w-full" disabled={busy || !comment.trim()} onClick={() => act(reviewDialog.plan.id, "revisar", { role: reviewDialog.role, comentario: comment })} data-testid="recovery-review-submit">
              {busy ? "Registrando…" : "Registrar revisión"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Tab: Recursos del proyecto ----------------

function RecursosTab({ pid }) {
  const [openAlert, setOpenAlert] = useState(null);
  const { data } = useQuery({ queryKey: ["plan-resources", pid], queryFn: async () => (await api.get(`/proyectos/${pid}/planning/recursos`)).data });
  const { data: actsData } = useQuery({ queryKey: ["plan-activities", pid, "vigente"], queryFn: async () => (await api.get(`/proyectos/${pid}/planning/actividades`, { params: { version: "vigente" } })).data });
  if (!data) return null;
  const actName = (aid) => actsData?.activities.find((a) => a.id === aid)?.name || (aid ? aid.slice(0, 18) : "asignación general");
  const resById = Object.fromEntries(data.resources.map((r) => [r.id, r]));
  const byResource = {};
  for (const a of data.allocations) byResource[a.resource_id] = [...(byResource[a.resource_id] || []), a];
  return (
    <div className="space-y-4" data-testid="recursos-tab">
      {data.alerts.length > 0 && (
        <div className="space-y-2" data-testid="resource-alerts">
          {data.alerts.map((al, i) => (
            <div key={i} className="rounded-sm border border-red-200 bg-red-50 p-3" data-testid={`resource-alert-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-red-900">{al.message}</p>
                {al.type === "sobreasignacion" && (
                  <Button size="sm" variant="outline" className="h-7 bg-white text-xs" onClick={() => setOpenAlert(openAlert === i ? null : i)} data-testid={`resource-alert-drill-${i}`}>
                    {openAlert === i ? "Ocultar" : "Ver asignaciones"}
                  </Button>
                )}
              </div>
              {openAlert === i && (
                <div className="mt-2 rounded-sm border border-red-100 bg-white p-2" data-testid={`resource-alert-detail-${i}`}>
                  <p className="mb-1 text-xs font-semibold text-slate-600">Asignaciones que generan el conflicto ({al.resource_name}, {al.period}):</p>
                  {(al.allocations || data.allocations.filter((a) => a.resource_id === al.resource_id && a.period === al.period)).map((a) => (
                    <p key={a.id} className="text-xs text-slate-600" data-testid={`resource-conflict-${a.id}`}>
                      <span className="font-mono font-bold text-red-700">{a.allocation_pct}%</span> — {a.activity_name || actName(a.activity_id)}
                      {a.project_name && a.project_id !== pid ? <span className="text-slate-400"> · {a.project_name}</span> : null}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="ts-card overflow-x-auto" data-testid="resource-allocations">
        <p className="border-b border-slate-100 px-4 py-3 font-display text-sm font-semibold">Asignaciones del proyecto ({data.allocations.length})</p>
        <Table>
          <TableHeader><TableRow><TableHead>Recurso</TableHead><TableHead>Rol</TableHead><TableHead>Actividad</TableHead><TableHead>Periodo</TableHead><TableHead className="text-right">Asignación</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.allocations.length === 0 && <TableRow><TableCell colSpan={5} className="py-5 text-center text-sm text-slate-500">Sin asignaciones de recursos.</TableCell></TableRow>}
            {data.allocations.map((a) => (
              <TableRow key={a.id} data-testid={`alloc-row-${a.id}`}>
                <TableCell className="text-xs font-medium">{resById[a.resource_id]?.name || a.resource_id}</TableCell>
                <TableCell className="text-xs">{resById[a.resource_id]?.role || "—"}</TableCell>
                <TableCell className="max-w-56 truncate text-xs">{actName(a.activity_id)}</TableCell>
                <TableCell className="font-mono text-xs">{a.period}</TableCell>
                <TableCell className={`text-right font-mono text-xs font-bold ${a.allocation_pct > 50 ? "text-red-600" : "text-slate-800"}`}>{a.allocation_pct}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------- Página principal ----------------

export default function Planificacion() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const pid = params.get("project");
  const tab = params.get("tab") || "planning";
  const { data: projects } = useQuery({ queryKey: ["proyectos"], queryFn: async () => (await api.get("/proyectos")).data });
  const { data: summary } = useQuery({
    queryKey: ["plan-summary", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/planning`)).data,
    enabled: !!pid,
  });
  const setTab = (t) => setParams({ project: pid, tab: t });
  const project = projects?.find((p) => p.id === pid);
  const canPlan = !!user && !!project && (
    (user.roles || []).some((r) => ["PROCESS_OWNER", "AREA_MANAGER", "EXECUTIVE", "DEMO_ADMIN"].includes(r)) ||
    [project.pm_id, project.planner_id, project.director_id].includes(user.id)
  );

  return (
    <div data-testid="planificacion-page" className="min-w-0 overflow-x-hidden">
      <PageHeader title="Planificación y control de avance"
        subtitle="Baseline inmutable, versiones con revisión y aprobación, dependencias FS/SS/FF/SF, desviaciones deterministas, riesgos de retraso y recovery plans."
        actions={
          <Select value={pid || ""} onValueChange={(v) => setParams({ project: v, tab })}>
            <SelectTrigger className="w-72" data-testid="project-selector"><SelectValue placeholder="Selecciona proyecto…" /></SelectTrigger>
            <SelectContent>
              {(projects || []).map((p) => <SelectItem key={p.id} value={p.id} data-testid={`project-opt-${p.id}`}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        } />

      {!pid && <EmptyState title="Selecciona un proyecto" description="Elige un proyecto para ver su planning, versiones, desviaciones, riesgos y recursos." testid="planning-no-project" />}

      {pid && project && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="planning-header">
            <Link to={`/proyectos/${pid}`} className="text-sm font-medium text-blue-700 hover:underline" data-testid="planning-project-link">{project.name}</Link>
            {summary?.vigente && <VersionChip version={summary.vigente} testid="header-vigente" />}
            {summary?.baseline && <span className="text-xs text-slate-400">baseline v{summary.baseline.number}</span>}
            {summary?.project_delay_days != null && (
              <span className={`ts-chip ${summary.project_delay_days > 0 ? "ts-chip-danger" : "ts-chip-success"}`} data-testid="header-delay">
                Desviación proyecto: {fmtDays(summary.project_delay_days)}
              </span>
            )}
            <span className="ts-chip ts-chip-warning" data-testid="header-risks">{summary?.open_risks ?? 0} riesgos abiertos</span>
            <span className="ts-chip ts-chip-info" data-testid="header-recovery">{summary?.open_recovery_plans ?? 0} recovery abiertos</span>
            <span className="ml-auto text-xs text-slate-400">Fecha de control: {fmtDate(project.control_date)}</span>
          </div>

          <div className="mb-4 flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
            {TABS.map((t) => (
              <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} data-testid={`plan-tab-${t.id}`}
                className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === t.id ? "border-blue-700 text-blue-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "planning" && <PlanningTab pid={pid} canPlan={canPlan} />}
          {tab === "versiones" && <VersionesTab pid={pid} canPlan={canPlan} />}
          {tab === "importar" && (canPlan ? <ImportarTab pid={pid} /> : <EmptyState title="Solo lectura" description="Tu perfil no puede importar planning en este proyecto." testid="import-no-permission" />)}
          {tab === "desviaciones" && <DesviacionesTab pid={pid} />}
          {tab === "riesgos" && <RiesgosTab pid={pid} canPlan={canPlan} />}
          {tab === "recovery" && <RecoveryTab pid={pid} canPlan={canPlan} />}
          {tab === "recursos" && <RecursosTab pid={pid} />}
        </>
      )}
      {pid && projects && !project && <EmptyState title="Proyecto no accesible" description="No existe o es confidencial y no perteneces a su equipo." testid="planning-no-access" />}
    </div>
  );
}
